import React, { useState } from "react";
import { Star, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

export const CourseFeedback = ({ instrumentId, courseId, defaultName = "" }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [name, setName] = useState(defaultName);
  const [allow, setAllow] = useState(true);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (comment.trim().length < 3) return toast.error("Please write a short comment");
    setBusy(true);
    try {
      await api.post("/feedback", { instrument: instrumentId, course_id: courseId, rating, comment: comment.trim(), display_name: name.trim(), allow_testimonial: allow });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send feedback");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-border bg-card p-8 text-center" data-testid="feedback-thanks">
        <Heart className="mx-auto text-primary" />
        <p className="mt-3 font-serif text-xl">Thank you for your feedback!</p>
        <p className="mt-1 text-sm text-muted-foreground">It's gone straight to Jason.</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="course-feedback">
      <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Course feedback</p>
      <h3 className="mt-2 font-serif text-2xl">How was this course?</h3>
      <div className="mt-4 flex gap-1" data-testid="feedback-stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} onClick={() => setRating(i)} aria-label={`${i} stars`} data-testid={`feedback-star-${i}`}>
            <Star size={26} className={`transition-colors ${i <= rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={1500} data-testid="feedback-comment"
        placeholder="What did you enjoy? What could be better?"
        className="mt-4 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Name to show (e.g. Sarah, London)" className="mt-3 border-border bg-background" data-testid="feedback-name" />
      <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} className="accent-[hsl(var(--primary))]" data-testid="feedback-allow-testimonial" />
        Jason may use this as a testimonial on the website
      </label>
      <Button onClick={submit} disabled={busy} className="mt-5 rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90" data-testid="feedback-submit-btn">
        <Send size={15} className="mr-2" /> {busy ? "Sending…" : "Send feedback"}
      </Button>
    </div>
  );
};
